import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import {
  DateFormat,
  DateUtilities,
  reformatDate,
  sleep,
  Utilities
} from '@approot/utilities';
import dayjs from 'dayjs';

@Component({
  selector: 'date-with-spinner-info',
  templateUrl: './date-with-spinner-info.component.html',
  styleUrls: ['./date-with-spinner-info.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DateWithSpinnerInfoComponent
  implements AfterViewInit, ControlValueAccessor, OnInit
{
  @ViewChild('dateInput', { static: false }) dateInput: ElementRef;
  @Input() public id: string;
  @Input() public inputValidatorKey: string[];
  @Input() public required: boolean;
  @Input() public readOnly: boolean;
  @Input() public info: string;
  @Input() public widthOfInput: number;
  @Input() public minDate: string = null;
  // @Input() public format: string = null; // dayjs pattern e.g., DD MMM YYYY
  @Input() public format: DateFormat;
  @Input() public textColor: string;
  @Input() public title: string = '';
  @Input() public formControlName: string;
  @Output() public inputChange = new EventEmitter<string>();
  @Output() public onCalendarChange = new EventEmitter<void>();
  @Output() public onBlur = new EventEmitter<void>();
  @Output() public onFocus = new EventEmitter<void>();
  private initialTextColor: string;

  constructor(public ngControl: NgControl) {
    ngControl.valueAccessor = this;
  }

  public ngOnInit(): void {
    this.initialTextColor = this.textColor;
  }

  public get formControl(): FormControl {
    return this.ngControl.control as FormControl;
  }

  public async ngAfterViewInit(): Promise<void> {
    await sleep(100);
    if (this.dateInput?.nativeElement) {
      // make the width of the spinner's parent the same as the input box
      if (this.widthOfInput == null) {
        this.widthOfInput = this.dateInput.nativeElement.offsetWidth;
      }

      /* Seems like clarity makes the input of the date element blank
      when it is being created (i believe due to the mismatch in date format).
      Setting the value back seems to fix this */
      const value = this.formControl?.value;
      if (value != null) {
        this.formControl.setValue(value, { emitEvent: false });
      }
    }
  }

  public onInputChange(event: Event): void {
    this.inputChange.emit((event.target as any).value);
  }

  public onElemBlur(): void {
    this.onBlur.emit();
  }

  public onElemFocus(): void {
    this.onFocus.emit();
  }

  public onClrDateChange(date: Date): void {
    this.inputChange.emit(DateUtilities.dateToPrettyDate(date));
    if (date) {
      // when date picker is used date has a value
      this.onCalendarChange.emit();
    }
  }

  public writeValue(value: string): void {
    if (!(this.format && value)) {
      return;
    }
    const newVal = reformatDate(value, this.format);
    if (['expiryDate', 'settlementDate'].includes(this.id)) {
      const today = dayjs().startOf('day');
      const d = dayjs(value).startOf('day');
      if (!d.isAfter(today)) {
        this.textColor = Utilities.orangeTextColor;
      } else {
        this.textColor = this.initialTextColor;
      }
    }
    if (!this.ngControl.control) {
      setTimeout(() => this.writeValue(value), 100);
    } else {
      if (newVal !== this.ngControl.control?.value) {
        setTimeout(
          () => this.ngControl.control?.setValue(newVal, { emitEvent: false }),
          10
        );
      }
    }
  }

  public onDateInputFocus(): void {
    /* If the date textbox is blured and it hasn't been touched previously,
    onChanges() seems to be triggered again, causing recalculations of the date.
    Setting the formControl as touched when focused, seems to fix this issue.
    */
    this.formControl.markAsTouched();
  }

  public registerOnChange(fn: any): void {}
  public registerOnTouched(fn: any): void {}
  public setDisabledState?(isDisabled: boolean): void {}
}
